// src/pages/GeofencingDashboard.tsx - ULTRA-COMPACT MOBILE VERSION
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as DownloadIcon,
  ContentCopy as CopyIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import api from '../services/api';
import GeofenceZoneDialog from '../components/GeofenceZoneDialog';
import BulkZoneDialog from '../components/BulkZoneDialog';
import ZoneTemplatesDialog from '../components/ZoneTemplatesDialog';
import GeofenceAnalyticsPanel from '../components/GeofenceAnalyticsPanel';
import ViolationPredictionWidget from '../components/ViolationPredictionWidget';

interface GeofenceZone {
  _id: string;
  name: string;
  type: 'circular' | 'polygon';
  isActive: boolean;
  rules: {
    alertOnEntry: boolean;
    alertOnExit: boolean;
    alertOnBreach: boolean;
    speedLimit?: number;
  };
  activeSchedule?: {
    enabled: boolean;
    days: number[];
    startTime: string;
    endTime: string;
  };
  overlapsDetected?: any[];
}

interface VehicleGeofenceStatus {
  _id: string;
  plateNumber: string;
  make: string;
  model: string;
  geofencingEnabled: boolean;
  zoneCount: number;
  currentStatus?: {
    isInside: boolean;
    activeZones: Array<{ zoneName: string }>;
  };
}

const GeofencingDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [vehicles, setVehicles] = useState<VehicleGeofenceStatus[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleGeofenceStatus | null>(null);
  const [zones, setZones] = useState<GeofenceZone[]>([]);
  const [violations, setViolations] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Dialog states
  const [openZoneDialog, setOpenZoneDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [openTemplatesDialog, setOpenTemplatesDialog] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [multiVehicleMode, setMultiVehicleMode] = useState(false);
  const [editingZone, setEditingZone] = useState<GeofenceZone | null>(null);

  useEffect(() => {
    fetchVehicles();
    fetchViolations();
    fetchStatistics();
    fetchAnalytics();
  }, []);

  useEffect(() => {
    if (selectedVehicle) {
      fetchZones(selectedVehicle._id);
      checkPredictions(selectedVehicle._id);
    }
  }, [selectedVehicle]);

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/geofencing/vehicles');
      setVehicles(response.data.data);
      if (response.data.data.length > 0) {
        setSelectedVehicle(response.data.data[0]);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async (vehicleId: string) => {
    try {
      console.log('🔍 Fetching zones for vehicle:', vehicleId);
      const response = await api.get(`/geofencing/vehicles/${vehicleId}/zones`);
      const zonesData = response.data.data?.zones || response.data.data || [];
     
       // ✅ ADD: More detailed logging
    console.log('✅ Zones received:', {
      count: zonesData.length,
      zones: zonesData.map((z: any) => ({
        id: z._id,
        name: z.name,
        type: z.type,
        center: z.center,
        radius: z.radius,
        isActive: z.isActive
      }))
    });


      setZones(zonesData);
    } catch (error) {
      console.error('❌ Error fetching zones:', error);
    }
  };

  const fetchViolations = async () => {
    try {
      const response = await api.get('/geofencing/violations?acknowledged=false&limit=10');
      setViolations(response.data.data);
    } catch (error) {
      console.error('Error fetching violations:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/geofencing/statistics');
      setStatistics(response.data.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/geofencing/analytics');
      setAnalytics(response.data.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const [predictions, setPredictions] = useState<any>(null);
  const checkPredictions = async (vehicleId: string) => {
    try {
      const response = await api.get(`/geofencing/vehicles/${vehicleId}/predict`);
      setPredictions(response.data.prediction);
    } catch (error) {
      console.error('Error checking predictions:', error);
    }
  };

  const toggleGeofencing = async (vehicleId: string, enabled: boolean) => {
    try {
      await api.put(`/geofencing/vehicles/${vehicleId}/toggle`, { enabled });
      fetchVehicles();
    } catch (error) {
      console.error('Error toggling geofencing:', error);
    }
  };

  const deleteZone = async (zoneId: string) => {
    if (!selectedVehicle) {
      console.error('❌ No selected vehicle');
      return;
    }

    if (!zoneId || zoneId === 'undefined') {
      console.error('❌ Invalid zone ID:', zoneId);
      alert('Cannot delete zone: Invalid zone ID');
      return;
    }

    console.log('🗑️ Deleting zone:', zoneId, 'from vehicle:', selectedVehicle._id);

    if (!window.confirm('Are you sure you want to delete this zone?')) return;

    try {
      const response = await api.delete(
        `/geofencing/vehicles/${selectedVehicle._id}/zones/${zoneId}`
      );
      console.log('✅ Zone deleted:', response.data);

      fetchZones(selectedVehicle._id);
      fetchStatistics();
      alert('Zone deleted successfully!');
    } catch (error: any) {
      console.error('❌ Delete error:', error.response?.data || error);
      alert(`Failed to delete: ${error.response?.data?.message || error.message}`);
    }
  };

  const duplicateZone = async (zone: GeofenceZone) => {
    if (!selectedVehicle) return;

    try {
      const newZone = {
        ...zone,
        name: `${zone.name} (Copy)`,
        _id: undefined
      };

      await api.post(`/geofencing/vehicles/${selectedVehicle._id}/zones`, newZone);
      fetchZones(selectedVehicle._id);
    } catch (error) {
      console.error('Error duplicating zone:', error);
    }
  };

  const acknowledgeViolation = async (vehicleId: string, violationId: string) => {
    try {
      await api.put(
        `/geofencing/vehicles/${vehicleId}/violations/${violationId}/acknowledge`,
        { notes: 'Reviewed and acknowledged' }
      );
      fetchViolations();
    } catch (error) {
      console.error('Error acknowledging violation:', error);
    }
  };

  const exportViolations = async (format: 'csv' | 'json') => {
    try {
      if (format === 'csv') {
        window.open(`${process.env.REACT_APP_API_URL}/api/geofencing/violations/export?format=csv`, '_blank');
      } else {
        const response = await api.get('/geofencing/violations/export?format=json');
        const dataStr = JSON.stringify(response.data.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `violations-${Date.now()}.json`;
        link.click();
      }
    } catch (error) {
      console.error('Error exporting violations:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: 0,
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden',
      bgcolor: 'background.default',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <Box sx={{ mb: 2, width: '100%' }}>
        <Typography
          variant={isMobile ? 'h6' : 'h4'}
          sx={{
            fontWeight: 'bold',
            mb: 1,
            width: '100%'
          }}
        >
          {isMobile ? 'Geofencing' : 'Geofencing Management'}
        </Typography>

        {/* Action buttons - Horizontal scroll on mobile */}
        {isMobile ? (
          <Box sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 1,
            width: '100%',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none'
          }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpenTemplatesDialog(true)}
              sx={{ minWidth: 'fit-content', px: 1.5, whiteSpace: 'nowrap' }}
            >
              Templates
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpenBulkDialog(true)}
              sx={{ minWidth: 'fit-content', px: 1.5, whiteSpace: 'nowrap' }}
            >
              Bulk
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpenExportDialog(true)}
              sx={{ minWidth: 'fit-content', px: 1.5, whiteSpace: 'nowrap' }}
            >
              Export
            </Button>
          </Box>
        ) : (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => setOpenTemplatesDialog(true)}
            >
              Templates
            </Button>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => setOpenBulkDialog(true)}
            >
              Bulk Manage
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => setOpenExportDialog(true)}
            >
              Export
            </Button>
          </Stack>
        )}
      </Box>

      {/* Tabs - FIXED: Prevent horizontal scroll */}
      <Box sx={{ width: '100%', overflowX: 'hidden', mb: 1.5 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{
            width: '100%',
            // ← FIXED: Ensure tabs fit properly
            '& .MuiTabs-flexContainer': {
              gap: isMobile ? 0.5 : 1,
            },
            '& .MuiTab-root': {
              minWidth: isMobile ? 70 : 'auto',
              px: isMobile ? 0.5 : 2,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              textTransform: 'none',
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Zones" />
          <Tab label="Violations" />
          <Tab
            label="Analytics"
            icon={<AnalyticsIcon sx={{ fontSize: isMobile ? 16 : 20 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab 0: Overview */}
      {activeTab === 0 && (
        <>
          {/* Statistics - 2x2 Grid on Mobile */}
          {statistics && (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{ bgcolor: 'background.paper' }}>  {/* ✅ ADD THIS */}
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {isMobile ? 'Vehicles' : 'Total Vehicles'}
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">
                      {statistics.totalVehicles}
                    </Typography>
                    {isMobile ? (
                      <Typography variant="caption" color="primary">
                        {statistics.vehiclesWithGeofencing} active
                      </Typography>
                    ) : (
                      <Chip
                        size="small"
                        label={`${statistics.vehiclesWithGeofencing} with geofencing`}
                        color="primary"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{ bgcolor: 'background.paper' }}>  {/* ✅ ADD THIS */}
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {isMobile ? 'Zones' : 'Active Zones'}
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" color="primary">
                      {statistics.activeZones}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {statistics.totalZones} total
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{ bgcolor: 'background.paper' }}>  {/* ✅ ADD THIS */}
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Violations
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" color="error">
                      {statistics.violations.total}
                    </Typography>
                    {isMobile ? (
                      <Typography variant="caption" color="warning.main">
                        {statistics.violations.unacknowledged} pending
                      </Typography>
                    ) : (
                      <Chip
                        size="small"
                        label={`${statistics.violations.unacknowledged} pending`}
                        color="warning"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
  <Card sx={{ bgcolor: 'background.paper' }}>  {/* ✅ ADD THIS */}
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Compliance
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" color="success.main">
                      {statistics.violations.total > 0
                        ? Math.round((1 - statistics.violations.unacknowledged / statistics.violations.total) * 100)
                        : 100}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Prediction Widget - Desktop Only */}
          {!isMobile && selectedVehicle && predictions?.hasPrediction && (
            <ViolationPredictionWidget prediction={predictions} vehicle={selectedVehicle} />
          )}

          <Grid container spacing={isMobile ? 1.5 : 3}>
            {/* Fleet Vehicles */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Fleet Vehicles
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {vehicles.map((vehicle) => (
                      <ListItem
                        key={vehicle._id}
                        button
                        selected={selectedVehicle?._id === vehicle._id}
                        onClick={() => setSelectedVehicle(vehicle)}
                        sx={{
                          border: 1,
                          borderColor: selectedVehicle?._id === vehicle._id ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          mb: 1,
                          p: isMobile ? 1 : 1.5,
                          bgcolor: selectedVehicle?._id === vehicle._id ? 'action.selected' : 'transparent'
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight="bold">
                                {vehicle.plateNumber}
                              </Typography>
                              {vehicle.geofencingEnabled ? (
                                <CheckCircleIcon color="success" sx={{ fontSize: isMobile ? 16 : 20 }} />
                              ) : (
                                <WarningIcon color="warning" sx={{ fontSize: isMobile ? 16 : 20 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                {vehicle.make} {vehicle.model}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  label={`${vehicle.zoneCount} zones`}
                                  sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                  color={vehicle.zoneCount > 0 ? 'primary' : 'default'}
                                />
                                {vehicle.currentStatus?.isInside && (
                                  <Chip
                                    size="small"
                                    label="Inside Zone"
                                    color="success"
                                    sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                  />
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Violations */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Recent Violations
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {violations.length === 0 ? (
                      <Typography variant="caption" color="textSecondary" align="center" sx={{ display: 'block', py: 2 }}>
                        No unacknowledged violations
                      </Typography>
                    ) : (
                      violations.slice(0, 5).map((violation) => (
                        <ListItem
                          key={violation._id}
                          sx={{
                            border: 1,
                            borderColor: 'error.light',
                            borderRadius: 1,
                            mb: 1,
                            p: isMobile ? 1 : 1.5,
                          }}
                        >
                          <ListItemText
                            primary={
                              <Stack spacing={0.5}>
                                <Typography variant={isMobile ? 'caption' : 'body2'} fontWeight="bold">
                                  {violation.plateNumber}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={violation.violationType.replace(/_/g, ' ')}
                                  color="error"
                                  sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                />
                              </Stack>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" display="block">
                                  Zone: {violation.zoneName}
                                </Typography>
                                <Typography variant="caption" display="block" color="textSecondary">
                                  {new Date(violation.timestamp).toLocaleString()}
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() =>
                                    acknowledgeViolation(
                                      violation.vehicleId,
                                      violation._id
                                    )
                                  }
                                  sx={{ mt: 0.5, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                  fullWidth={isMobile}
                                >
                                  Acknowledge
                                </Button>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Tab 1: Zones Management */}
      {activeTab === 1 && selectedVehicle && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold" sx={{ mb: isMobile ? 1 : 0 }}>
                    Zones for {selectedVehicle.plateNumber}
                  </Typography>

                  <Stack direction={isMobile ? 'column' : 'row'} spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color={vehicles.every(v => v.geofencingEnabled) ? 'error' : 'success'}
                      onClick={async () => {
                        const allEnabled = vehicles.every(v => v.geofencingEnabled);
                        const action = allEnabled ? 'disable' : 'enable';

                        if (!window.confirm(`${action} geofencing for all ${vehicles.length} vehicles?`)) return;

                        try {
                          for (const vehicle of vehicles) {
                            await api.put(`/geofencing/vehicles/${vehicle._id}/enable`, {
                              enabled: !allEnabled
                            });
                          }

                          await fetchVehicles();
                          alert(`Geofencing ${action}d for all vehicles!`);
                        } catch (error: any) {
                          alert(`Failed: ${error.response?.data?.message || error.message}`);
                        }
                      }}
                      fullWidth={isMobile}
                    >
                      {vehicles.every(v => v.geofencingEnabled) ? 'Disable All' : 'Enable All'}
                    </Button>

                    <Button
                      variant="contained"
                      size="small"
                      startIcon={!isMobile && <AddIcon />}
                      onClick={() => {
                        setEditingZone(null);
                        setOpenZoneDialog(true);
                      }}
                      fullWidth={isMobile}
                    >
                      Add Zone
                    </Button>
                  </Stack>
                </Box>

                {/* Geofencing Toggle */}
                <Alert
                  severity={selectedVehicle.geofencingEnabled ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                  icon={selectedVehicle.geofencingEnabled ? <CheckCircleIcon /> : <WarningIcon />}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 1 : 0
                  }}>
                    <Typography variant={isMobile ? 'caption' : 'body2'}>
                      Geofencing is {selectedVehicle.geofencingEnabled ? 'enabled' : 'disabled'}
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedVehicle.geofencingEnabled}
                          onChange={async (e) => {
                            const enabled = e.target.checked;

                            try {
                              await api.put(`/geofencing/vehicles/${selectedVehicle._id}/enable`, { enabled });
                              await fetchVehicles();
                              setSelectedVehicle({
                                ...selectedVehicle,
                                geofencingEnabled: enabled
                              });
                              alert(`Geofencing ${enabled ? 'enabled' : 'disabled'}`);
                            } catch (error: any) {
                              alert(`Failed: ${error.response?.data?.message || error.message}`);
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={selectedVehicle.geofencingEnabled ? 'Enabled' : 'Disabled'}
                    />
                  </Box>
                </Alert>

                {/* Zone List */}
                <List sx={{ p: 0 }}>
                  {zones.map((zone) => (
                    <ListItem
                      key={zone._id}
                      sx={{
                        border: 1,
                        borderColor: zone.isActive ? 'success.light' : 'divider',
                        borderRadius: 1,
                        mb: 1,
                        p: isMobile ? 1 : 1.5,
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'center'
                      }}
                    >
                      <ListItemText
                        primary={
                          <Stack spacing={0.5}>
                            <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="bold">
                              {zone.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                size="small"
                                label={zone.type}
                                sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                              />
                              {zone.isActive ? (
                                <Chip
                                  size="small"
                                  label="Active"
                                  color="success"
                                  sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  label="Inactive"
                                  sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                />
                              )}
                              {zone.activeSchedule?.enabled && (
                                <Chip
                                  size="small"
                                  label="Scheduled"
                                  color="info"
                                  sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                />
                              )}
                              {zone.rules.speedLimit && (
                                <Chip
                                  size="small"
                                  label={`${zone.rules.speedLimit} km/h`}
                                  sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                />
                              )}
                            </Box>
                          </Stack>
                        }
                      />

                      <Box sx={{
                        display: 'flex',
                        gap: 0.5,
                        mt: isMobile ? 1 : 0,
                        width: isMobile ? '100%' : 'auto',
                        justifyContent: isMobile ? 'flex-end' : 'flex-start'
                      }}>
                        <IconButton
                          size="small"
                          onClick={() => duplicateZone(zone)}
                          title="Duplicate zone"
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingZone(zone);
                            setOpenZoneDialog(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteZone(zone._id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                  {zones.length === 0 && (
                    <Typography variant="caption" color="textSecondary" align="center" sx={{ display: 'block', py: 4 }}>
                      No zones configured. Click "Add Zone" to create one.
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Violations */}
      {activeTab === 2 && (
        <Card>
          <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 1.5 : 0
            }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold">
                All Violations
              </Typography>
              <Button
                variant="outlined"
                startIcon={!isMobile && <DownloadIcon />}
                onClick={() => setOpenExportDialog(true)}
                size="small"
                fullWidth={isMobile}
              >
                Export
              </Button>
            </Box>
            <List sx={{ p: 0 }}>
              {violations.map((violation) => (
                <ListItem
                  key={violation._id}
                  sx={{
                    border: 1,
                    borderColor: 'error.light',
                    borderRadius: 1,
                    mb: 1,
                    p: isMobile ? 1 : 1.5,
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack spacing={0.5}>
                        <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight="bold">
                          {violation.plateNumber} - {violation.make} {violation.model}
                        </Typography>
                        <Chip
                          size="small"
                          label={violation.violationType.replace(/_/g, ' ')}
                          color="error"
                          sx={{ height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                        />
                      </Stack>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          Zone: <strong>{violation.zoneName}</strong>
                        </Typography>
                        {violation.speed && (
                          <Typography variant="caption" display="block">
                            Speed: <strong>{Math.round(violation.speed)} km/h</strong>
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary" display="block">
                          {new Date(violation.timestamp).toLocaleString()}
                        </Typography>
                        {!violation.acknowledgedBy && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              acknowledgeViolation(violation.vehicleId, violation._id)
                            }
                            sx={{ mt: 0.5, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                            fullWidth={isMobile}
                          >
                            Acknowledge
                          </Button>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Tab 3: Analytics */}
      {activeTab === 3 && analytics && (
        <GeofenceAnalyticsPanel analytics={analytics} onRefresh={fetchAnalytics} />
      )}

      {/* Dialogs */}
      <GeofenceZoneDialog
        open={openZoneDialog}
        onClose={() => {
          setOpenZoneDialog(false);
          setEditingZone(null);
          setMultiVehicleMode(false);
        }}
        vehicleId={multiVehicleMode ? undefined : selectedVehicle?._id}
        zoneData={editingZone}
        onSuccess={() => {
          setOpenZoneDialog(false);
          setEditingZone(null);
          setMultiVehicleMode(false);
          if (selectedVehicle) {
            fetchZones(selectedVehicle._id);
          }
          fetchVehicles();
          fetchStatistics();
        }}
      />

      <BulkZoneDialog
        open={openBulkDialog}
        onClose={() => setOpenBulkDialog(false)}
        vehicles={vehicles}
        onSuccess={() => {
          setOpenBulkDialog(false);
          fetchVehicles();
        }}
      />

      <ZoneTemplatesDialog
        open={openTemplatesDialog}
        onClose={() => setOpenTemplatesDialog(false)}
        onSelect={(template) => {
          setOpenTemplatesDialog(false);
          setOpenZoneDialog(true);
        }}
      />

      {/* Export Dialog - Mobile Responsive */}
      <Dialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Export Violations</Typography>
            {isMobile && (
              <IconButton onClick={() => setOpenExportDialog(false)} size="small">
                <DownloadIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose export format:
          </Typography>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                exportViolations('csv');
                setOpenExportDialog(false);
              }}
            >
              CSV Format
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                exportViolations('json');
                setOpenExportDialog(false);
              }}
            >
              JSON Format
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          p: 2,
          '& > button': {
            width: isMobile ? '100%' : 'auto'
          }
        }}>
          <Button onClick={() => setOpenExportDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GeofencingDashboard;